import type { Assessment } from "../assessment-types";

export const EMOTIONAL_REGULATION_ASSESSMENT: Assessment = {
  slug: "emotional-regulation",
  emoji: "🌊",
  title: "Emotional Regulation",
  tagline: "How feelings arrive, build and settle",
  intro:
    "This reflects on how emotions tend to move through you — how quickly they build, how big they feel, and how long it takes to settle again. There are no right answers, just patterns worth noticing.",
  questions: [
    { id: "q1", text: "Do you find your emotions go from calm to intense very quickly, with little warning?" },
    { id: "q2", text: "Once you're upset, does it feel hard to bring yourself back down?" },
    { id: "q3", text: "Do small things sometimes trigger a reaction that feels bigger than the moment?" },
    { id: "q4", text: "Do you find yourself avoiding situations because you're not sure how you'll react?" },
    { id: "q5", text: "After a strong emotion passes, do you feel drained or wiped out?" },
    { id: "q6", text: "Do you struggle to name what you're feeling in the moment?" },
    { id: "q7", text: "Do you feel like you swing between feeling too much and feeling nothing at all?" },
    { id: "q8", text: "Do you find it hard to calm yourself down without distraction or someone else's help?" },
    { id: "q9", text: "Do you replay upsetting moments long after they've happened?" },
    { id: "q10", text: "Do you feel embarrassed or ashamed after an emotional reaction?" },
    { id: "q11", text: "Do you notice your body reacting (tight chest, racing heart) before you know why?" },
    { id: "q12", text: "Do you feel like your emotional reactions are stronger than those of people around you?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your emotions seem to move through fairly steadily most of the time.",
      body: "This suggests a good deal of ease with how feelings arrive and settle. Everyone has harder days, and it's still worth noticing what helps you stay steady.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some patterns related to emotions building quickly and taking a while to settle.",
      body: "This is common and often develops when big feelings weren't met with steady support early on. Learning to notice emotions as they build, rather than after they peak, can make a real difference.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a pattern of emotions feeling intense and hard to bring back down.",
      body: "This often reflects a nervous system that learned to react fast, sometimes because calm wasn't modelled or safe growing up. It made sense as a way of staying alert to what mattered. Gentle grounding practices can help build a sense of steadiness over time.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong pattern of emotional waves that feel overwhelming and slow to settle.",
      body: "This can be exhausting, and it often has roots in a history where feelings weren't safely soothed. It's an adaptation, not a flaw. With practice, it's possible to widen the space between a feeling arriving and it taking over.",
    },
  ],
  next: [
    { label: "Understanding the window of tolerance", to: "/learn/window-of-tolerance" },
    { label: "Emotional flashbacks explained", to: "/learn/emotional-flashbacks" },
    { label: "Try a grounding exercise", to: "/grounding" },
    { label: "Try guided breathing", to: "/breathing" },
  ],
};
